import { useState, useEffect, useRef } from "react";
import { patientAPI } from "../utils/api";
import { useApp } from "../context/AppContext";

const BLOOD_GROUPS = ["A+","A-","B+","B-","AB+","AB-","O+","O-","Unknown"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function validatePhoneDigits(digits) {
  const d = digits.trim().replace(/[\s\-()]/g, "");
  if (!d) return { valid: false, message: "Phone number is required." };
  if (!/^\d{10}$/.test(d)) return { valid: false, message: "Enter exactly 10 digits (e.g. 1712345678)." };
  if (!/^1[3-9]\d{8}$/.test(d)) return { valid: false, message: "Must start with 13–19 (e.g. 17, 18, 19, 16, 15, 13, 14)." };
  return { valid: true, normalised: "+880" + d };
}

// Compute display age string from DOB
function ageFromDob(dob) {
  if (!dob) return null;
  const birth = new Date(dob + "T00:00:00");
  const today = new Date();
  if (isNaN(birth.getTime()) || birth >= today) return null;
  let years  = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth()    - birth.getMonth();
  if (today.getDate() < birth.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  if (years < 0)  return null;
  if (years === 0 && months === 0) {
    const days = Math.floor((today - birth) / (1000 * 60 * 60 * 24));
    return days === 1 ? "1 Day" : `${days} Days`;
  }
  if (years === 0)  return months === 1 ? "1 Month" : `${months} Months`;
  if (years < 3)    return `${years}Y ${months}M`;
  return `${years}Y`;
}

// ── Custom DOB Date Picker ────────────────────────────────────────────────────
function DobPicker({ value, onChange, error }) {
  const today     = new Date();
  const todayStr  = today.toISOString().split("T")[0];
  const todayYear = today.getFullYear();
  const todayMonth= today.getMonth();     // 0-indexed
  const todayDay  = today.getDate();

  // Parse selected value
  const selDate  = value ? new Date(value + "T00:00:00") : null;
  const selYear  = selDate ? selDate.getFullYear() : null;
  const selMonth = selDate ? selDate.getMonth()    : null; // 0-indexed
  const selDay   = selDate ? selDate.getDate()     : null;

  // Calendar nav state — default to selected date or today
  const [viewYear,  setViewYear]  = useState(selYear  ?? todayYear);
  const [viewMonth, setViewMonth] = useState(selMonth ?? todayMonth);
  const [calView,   setCalView]   = useState("days"); // "days" | "monthyear"
  const [open,      setOpen]      = useState(false);
  const pickerRef = useRef(null);
  const yearListRef = useRef(null);

  // Sync view to selected date when value changes externally
  useEffect(() => {
    if (selYear)  setViewYear(selYear);
    if (selMonth != null) setViewMonth(selMonth);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setOpen(false);
        setCalView("days");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Scroll active year into view when monthyear panel opens.
  // Targets selected year if one exists, else the current viewYear.
  useEffect(() => {
    if (calView === "monthyear" && yearListRef.current) {
      const el = yearListRef.current.querySelector("[data-active='true']");
      if (el) el.scrollIntoView({ block: "center" });
    }
  }, [calView]);

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayOfMonth = (y, m) => new Date(y, m, 1).getDay(); // 0=Sun

  const isFuture = (y, m, d) => {
    if (y > todayYear) return true;
    if (y === todayYear && m > todayMonth) return true;
    if (y === todayYear && m === todayMonth && d > todayDay) return true;
    return false;
  };
  const isMonthFuture = (y, m) => y > todayYear || (y === todayYear && m > todayMonth);

  const selectDay = (y, m, d) => {
    if (isFuture(y, m, d)) return;
    const iso = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    onChange(iso);
    setOpen(false);
    setCalView("days");
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y-1); setViewMonth(11); }
    else setViewMonth(m => m-1);
  };
  const nextMonth = () => {
    if (isMonthFuture(viewYear, viewMonth)) return;
    if (viewMonth === 11) { setViewYear(y => y+1); setViewMonth(0); }
    else setViewMonth(m => m+1);
  };

  const canGoNext = !isMonthFuture(viewYear, viewMonth);

  // Build day grid
  const firstDay = firstDayOfMonth(viewYear, viewMonth);
  const days     = daysInMonth(viewYear, viewMonth);
  const prevDays = daysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1);
  const cells    = [];
  // Leading blank cells from prev month
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({ day: prevDays - i, month: viewMonth === 0 ? 11 : viewMonth - 1, year: viewMonth === 0 ? viewYear - 1 : viewYear, other: true });
  // Current month days
  for (let d = 1; d <= days; d++)
    cells.push({ day: d, month: viewMonth, year: viewYear, other: false });
  // Trailing cells from next month
  const needed = Math.ceil(cells.length / 7) * 7;
  let nd = 1;
  while (cells.length < needed)
    cells.push({ day: nd++, month: viewMonth === 11 ? 0 : viewMonth + 1, year: viewMonth === 11 ? viewYear + 1 : viewYear, other: true });

  // Year list: ascending (oldest at top, newest at bottom).
  // Most adult patients born 1950-2010 cluster near the bottom;
  // auto-scroll always centers the selected year on open.
  const years = [];
  for (let y = todayYear - 120; y <= todayYear; y++) years.push(y);

  const displayStr = selDate
    ? `${String(selMonth+1).padStart(2,"0")} / ${String(selDay).padStart(2,"0")} / ${selYear}`
    : null;

  const S = {
    wrap:       { position:"relative" },
    input:      { display:"flex", alignItems:"center", width:"100%", background:"#fff", border:`1px solid ${error ? "#EF4444" : open ? "#4F46E5" : "#CBD5E1"}`, borderRadius:6, padding:"9px 12px", cursor:"pointer", fontSize:13, color:"#1E293B", boxShadow: open ? "0 0 0 2px #EEF2FF" : "0 1px 2px rgba(0,0,0,.06)", userSelect:"none", boxSizing:"border-box", gap:8 },
    placeholder:{ color:"#94A3B8", flex:1 },
    popover:    { position:"absolute", top:"calc(100% + 6px)", left:0, width:280, background:"#fff", border:"1px solid #E2E8F0", borderRadius:10, boxShadow:"0 10px 40px rgba(0,0,0,.14)", zIndex:999, padding:12, userSelect:"none" },
    header:     { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, padding:"0 4px" },
    monthBtn:   { display:"flex", alignItems:"center", gap:4, fontWeight:700, fontSize:15, color:"#0F172A", background:"none", border:"none", cursor:"pointer", padding:"4px 8px", borderRadius:6 },
    navBtn:     { width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", background:"none", border:"none", cursor:"pointer", borderRadius:6, color:"#64748B" },
    dayHeader:  { display:"grid", gridTemplateColumns:"repeat(7,1fr)", textAlign:"center", fontSize:11, fontWeight:600, color:"#475569", marginBottom:6 },
    dayGrid:    { display:"grid", gridTemplateColumns:"repeat(7,1fr)", textAlign:"center", gap:"2px 1px" },
    footer:     { display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:14, paddingTop:10, borderTop:"1px solid #F1F5F9", padding:"10px 4px 0" },
    yearScroll: { height:128, overflowY:"auto", background:"#F8FAFC", borderRadius:6, marginBottom:12, border:"1px solid #E2E8F0" },
    monthGrid:  { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:4, fontSize:13, textAlign:"center", fontWeight:500 },
  };

  return (
    <div style={S.wrap} ref={pickerRef}>
      {/* Input trigger */}
      <div style={S.input} onClick={() => { setOpen(o => !o); if (!open) setCalView("days"); }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        {displayStr
          ? <span style={{ flex:1, fontWeight:600, letterSpacing:".04em" }}>{displayStr}</span>
          : <span style={S.placeholder}>mm / dd / yyyy</span>}
        {value && (
          <span onMouseDown={e => { e.stopPropagation(); onChange(""); setOpen(false); }}
            style={{ color:"#94A3B8", cursor:"pointer", fontSize:16, lineHeight:1, padding:"0 2px" }}>×</span>
        )}
      </div>

      {/* Popover */}
      {open && (
        <div style={S.popover}>
          <style>{`.dob-year-scroll::-webkit-scrollbar{width:8px}.dob-year-scroll::-webkit-scrollbar-track{background:#F1F5F9;border-radius:4px}.dob-year-scroll::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:4px}.dob-year-scroll::-webkit-scrollbar-thumb:hover{background:#94A3B8}`}</style>
          {calView === "days" ? (
            <>
              {/* Header */}
              <div style={S.header}>
                <button style={S.monthBtn} onClick={() => setCalView("monthyear")}>
                  {MONTHS[viewMonth]} {viewYear}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <div style={{ display:"flex", gap:4 }}>
                  <button style={S.navBtn} onClick={prevMonth} title="Previous month"
                    onMouseEnter={e => e.currentTarget.style.background="#F1F5F9"}
                    onMouseLeave={e => e.currentTarget.style.background="none"}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                  </button>
                  <button style={{ ...S.navBtn, opacity: canGoNext ? 1 : 0.3, cursor: canGoNext ? "pointer" : "not-allowed" }}
                    onClick={nextMonth} title="Next month"
                    onMouseEnter={e => { if(canGoNext) e.currentTarget.style.background="#F1F5F9"; }}
                    onMouseLeave={e => e.currentTarget.style.background="none"}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                </div>
              </div>

              {/* Day headers */}
              <div style={S.dayHeader}>
                {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d}>{d}</div>)}
              </div>

              {/* Day grid */}
              <div style={S.dayGrid}>
                {cells.map((c, i) => {
                  const isSel = !c.other && c.year === selYear && c.month === selMonth && c.day === selDay;
                  const fut   = isFuture(c.year, c.month, c.day);
                  const isToday = !c.other && c.year === todayYear && c.month === todayMonth && c.day === todayDay;
                  return (
                    <div key={i} onClick={() => !fut && !c.other && selectDay(c.year, c.month, c.day)}
                      style={{
                        padding:"6px 2px", borderRadius:4, fontSize:13, cursor: c.other || fut ? "default" : "pointer",
                        color: isSel ? "#fff" : c.other ? "#CBD5E1" : fut ? "#CBD5E1" : "#1E293B",
                        background: isSel ? "#4F46E5" : "none",
                        fontWeight: isSel ? 700 : isToday ? 600 : 400,
                        border: isToday && !isSel ? "1.5px solid #4F46E5" : isSel ? "2px solid #1E1B4B" : "none",
                        opacity: c.other ? 0.45 : 1,
                      }}
                      onMouseEnter={e => { if (!c.other && !fut && !isSel) e.currentTarget.style.background="#F1F5F9"; }}
                      onMouseLeave={e => { if (!isSel) e.currentTarget.style.background="none"; }}>
                      {c.day}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={S.footer}>
                <button style={{ background:"none", border:"none", cursor: value ? "pointer" : "default", color: value ? "#4F46E5" : "#CBD5E1", fontSize:13, fontWeight:600, padding:0 }}
                  onClick={() => { if(value) { onChange(""); setOpen(false); } }}>Clear</button>
                <button style={{ background:"none", border:"none", cursor:"default", color:"#CBD5E1", fontSize:13, fontWeight:600, padding:0 }}>Today</button>
              </div>
            </>
          ) : (
            <>
              {/* Month-year selector view */}
              <div style={{ ...S.header, marginBottom:10 }}>
                <button style={S.monthBtn} onClick={() => setCalView("days")}>
                  {MONTHS[viewMonth]} {viewYear}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transform:"rotate(180deg)" }}><path d="M6 9l6 6 6-6"/></svg>
                </button>
              </div>

              {/* Year list */}
              <div style={S.yearScroll} ref={yearListRef} className="dob-year-scroll">
                {years.map(y => {
                  const isSel = y === viewYear;
                  return (
                    <div key={y} data-active={isSel || y === viewYear ? "true" : "false"} data-selected={isSel ? "true" : "false"}
                      onClick={() => { setViewYear(y); }}
                      style={{ padding:"7px 16px", fontSize:14, cursor:"pointer", fontWeight: isSel ? 700 : 400,
                        color: isSel ? "#1E293B" : "#475569", background: isSel ? "#E2E8F0" : "none",
                        borderBottom:"1px solid #F1F5F9" }}
                      onMouseEnter={e => { if(!isSel) e.currentTarget.style.background="#F1F5F9"; }}
                      onMouseLeave={e => { if(!isSel) e.currentTarget.style.background="none"; }}>
                      {y}
                    </div>
                  );
                })}
              </div>

              {/* Month grid */}
              <div style={S.monthGrid}>
                {MONTHS.map((m, mi) => {
                  const fut  = isMonthFuture(viewYear, mi);
                  const isSel = viewYear === selYear && mi === selMonth;
                  const isView = viewYear === viewYear && mi === viewMonth;
                  return (
                    <div key={m}
                      onClick={() => { if (!fut) { setViewMonth(mi); setCalView("days"); } }}
                      style={{ padding:"8px 4px", borderRadius:4, cursor: fut ? "not-allowed" : "pointer",
                        color: isSel ? "#fff" : fut ? "#CBD5E1" : "#1E293B",
                        background: isSel ? "#4F46E5" : "none",
                        fontWeight: isSel ? 700 : 400,
                        border: isSel ? "2px solid #1E1B4B" : isView && !isSel ? "1px solid #CBD5E1" : "none",
                        opacity: fut ? 0.4 : 1 }}
                      onMouseEnter={e => { if(!isSel && !fut) e.currentTarget.style.background="#F1F5F9"; }}
                      onMouseLeave={e => { if(!isSel) e.currentTarget.style.background="none"; }}>
                      {m}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function RegisterPatient({ onClose, onDone, onOpenPatient }) {
  const { addPatient } = useApp();
  const [form, setForm] = useState({
    name:"", dob:"", dobUnknown:false, age:"",
    sex:"Female", blood:"B+",
    phoneDigits:"", address:"", doctor:"", emergency_contact:"", notes:"",
    smokingStatus:"Never", smokingDetail:"",
    betelNut:"No", betelNutDetail:"",
    conditions:[], ordered_tests:[]
  });
  const [conditionInput, setConditionInput] = useState("");
  const [testSearch, setTestSearch]         = useState("");
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors]   = useState({});
  const [error, setError]     = useState("");
  const [duplicates, setDuplicates]       = useState([]);
  const [dupChecking, setDupChecking]     = useState(false);
  const [proceedAnyway, setProceedAnyway] = useState(false);
  const [testGroups, setTestGroups]       = useState([]);
  const dupDebounce = useRef(null);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: null }));
    if (k === "name" || k === "phoneDigits") setProceedAnyway(false);
  };

  // Load test subtypes from backend for ordered tests selection
  useEffect(() => {
    patientAPI.getOrderedTestSubtypes()
      .then(d => setTestGroups(d.groups || []))
      .catch(() => {});
  }, []);

  const toggleTest = (testName) => {
    setForm(f => {
      const tests = f.ordered_tests || [];
      return {
        ...f,
        ordered_tests: tests.includes(testName)
          ? tests.filter(t => t !== testName)
          : [...tests, testName]
      };
    });
  };

  useEffect(() => {
    clearTimeout(dupDebounce.current);
    const phoneResult = validatePhoneDigits(form.phoneDigits);
    if (!phoneResult.valid) { setDuplicates([]); return; }
    setDupChecking(true);
    dupDebounce.current = setTimeout(async () => {
      try {
        const res = await patientAPI.checkDuplicate(form.name.trim(), phoneResult.normalised);
        setDuplicates(res.duplicates || []);
      } catch { setDuplicates([]); }
      finally { setDupChecking(false); }
    }, 600);
    return () => clearTimeout(dupDebounce.current);
  }, [form.phoneDigits, form.name]);

  const handleDobUnknownToggle = (unknown) => {
    setForm(f => ({ ...f, dobUnknown: unknown, dob: unknown ? "" : f.dob, age: unknown ? f.age : "" }));
    setErrors(e => ({ ...e, dob: null, age: null }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Full name is required.";
    if (form.dobUnknown) {
      // Age fallback mode
      if (!form.age) errs.age = "Estimated age is required.";
      else {
        const n = parseInt(form.age);
        if (isNaN(n) || n < 0 || n > 150) errs.age = "Age must be between 0 and 150.";
      }
    } else {
      if (!form.dob) errs.dob = "Date of birth is required.";
      else {
        const birth = new Date(form.dob + "T00:00:00");
        if (isNaN(birth.getTime()) || birth >= new Date()) errs.dob = "Enter a valid past date.";
      }
    }
    const phoneResult = validatePhoneDigits(form.phoneDigits);
    if (!phoneResult.valid) errs.phoneDigits = phoneResult.message;
    if (!form.address.trim()) errs.address = "Address is required.";
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!form.doctor.trim()) errs.doctor = "Referred doctor is required.";
    return errs;
  };

  const goNext = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (dupChecking) { setErrors({ _dup: "Please wait — checking for existing patients..." }); return; }
    if (duplicates.length > 0 && !proceedAnyway) { setErrors({ _dup: "Please review the possible duplicate patients below before continuing." }); return; }
    setErrors({});
    setStep(2);
  };

  const submit = async () => {
    const step2Errs = validateStep2();
    if (Object.keys(step2Errs).length) { setErrors(step2Errs); return; }
    setLoading(true); setError("");
    try {
      const phoneResult = validatePhoneDigits(form.phoneDigits);
      const phone = phoneResult.valid ? phoneResult.normalised : "";
      const notesText = form.conditions.length > 0 ? form.conditions.join(", ") : form.notes;
      // If DOB known: store dob + age string. If unknown: store age as string e.g. "36Y"
      const dobVal = form.dobUnknown ? null : (form.dob || null);
      const ageVal = form.dobUnknown
        ? (form.age ? `${parseInt(form.age)}Y` : null)
        : (form.dob ? ageFromDob(form.dob) : null);
      const payload = {
        ...form,
        phone,
        dob:  dobVal,
        age:  ageVal,
        notes: notesText,
        smoking_status: form.smokingStatus,
        smoking_detail: form.smokingDetail || null,
        betel_nut: form.betelNut,
        betel_nut_detail: form.betelNutDetail || null,
        ordered_tests: form.ordered_tests && form.ordered_tests.length > 0 ? form.ordered_tests : null,
      };
      const id = await addPatient(payload);
      if (form.conditions.length > 0) {
        const { historyAPI } = await import("../utils/api");
        for (const cond of form.conditions) {
          try { await historyAPI.add(id, { type: "Chronic Condition", narrative: cond, recorded_by: "Reception (Intake)" }); } catch(e) {}
        }
      }
      if (form.smokingStatus !== "Never" || form.betelNut === "Yes") {
        const { historyAPI } = await import("../utils/api");
        const socialNarrative = [
          form.smokingStatus !== "Never" ? `Smoking: ${form.smokingStatus}${form.smokingDetail ? " — " + form.smokingDetail : ""}` : null,
          form.betelNut === "Yes" ? `Betel Nut/Jorda: Yes${form.betelNutDetail ? " — " + form.betelNutDetail : ""}` : null,
        ].filter(Boolean).join(" | ");
        try { await historyAPI.add(id, { type: "Social", narrative: socialNarrative, recorded_by: "Reception (Intake)" }); } catch(e) {}
      }
      onDone(id);
    } catch (e) {
      setError(e.message || "Registration failed");
      setLoading(false);
    }
  };

  const phoneResult = validatePhoneDigits(form.phoneDigits);
  const phonePreview = phoneResult.valid ? phoneResult.normalised : (form.phoneDigits ? `+880${form.phoneDigits}` : "+880__________");
  const ageDisplay = form.dob ? ageFromDob(form.dob) : null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div>
            <div style={{ fontWeight:700, fontSize:16 }}>Register New Patient</div>
            <div style={{ fontSize:12, color:"#64748B" }}>Step {step} of 2</div>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding:"10px 24px 0", display:"flex", gap:6 }}>
          {[1,2].map(s => (
            <div key={s} style={{ flex:1, height:3, borderRadius:99, background: s<=step ? "#4F46E5" : "#E4E8F0", transition:"background 0.2s" }}/>
          ))}
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-red">{error}</div>}

          {step === 1 && (
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:"#475569", marginBottom:16, textTransform:"uppercase", letterSpacing:"0.5px" }}>Personal Information</div>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className={`input ${errors.name ? "input-error" : ""}`} value={form.name} onChange={e => set("name",e.target.value)} placeholder="Patient's full name" autoFocus />
                {errors.name && <div style={{ color:"#DC2626", fontSize:12, marginTop:3 }}>{errors.name}</div>}
              </div>

              {/* ── DOB / Age field ── */}
              <div className="form-group">
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <label className="form-label" style={{ marginBottom:0 }}>
                    {form.dobUnknown ? "Estimated Age *" : "Date of Birth *"}
                  </label>
                  {/* OpenMRS-style toggle */}
                  <button type="button"
                    onClick={() => handleDobUnknownToggle(!form.dobUnknown)}
                    style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"#4F46E5", fontWeight:600, padding:0, textDecoration:"underline" }}>
                    {form.dobUnknown ? "I know the date of birth" : "Date of birth unknown"}
                  </button>
                </div>

                {form.dobUnknown ? (
                  // Fallback: estimated age in years/months
                  <div style={{ display:"flex", gap:8 }}>
                    <div style={{ flex:1 }}>
                      <input className={`input ${errors.age ? "input-error" : ""}`}
                        type="number" min="0" max="150" placeholder="Years e.g. 26"
                        value={form.age} onChange={e => set("age", e.target.value)} />
                    </div>
                    <div style={{ display:"flex", alignItems:"center", fontSize:12, color:"#64748B", whiteSpace:"nowrap" }}>
                      (approximate)
                    </div>
                  </div>
                ) : (
                  // Primary: custom date picker
                  <DobPicker
                    value={form.dob}
                    onChange={v => set("dob", v)}
                    error={!!errors.dob}
                  />
                )}

                {errors.dob && <div style={{ color:"#DC2626", fontSize:12, marginTop:4 }}>{errors.dob}</div>}
                {errors.age && <div style={{ color:"#DC2626", fontSize:12, marginTop:4 }}>{errors.age}</div>}

                {/* Age computed from DOB */}
                {!form.dobUnknown && ageDisplay && (
                  <div style={{ fontSize:11, color:"#4F46E5", fontWeight:600, marginTop:4 }}>
                    Age: {ageDisplay}
                  </div>
                )}
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Sex *</label>
                  <select className="select" value={form.sex} onChange={e => set("sex",e.target.value)}>
                    <option>Female</option><option>Male</option><option>Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Blood Group</label>
                  <select className="select" value={form.blood} onChange={e => set("blood",e.target.value)}>
                    {BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <div style={{ display:"flex", alignItems:"stretch" }}>
                  <span style={{ padding:"0 10px", display:"flex", alignItems:"center", background:"#F1F5F9", border:"1px solid #E4E8F0", borderRight:"none", borderRadius:"6px 0 0 6px", fontSize:13, fontWeight:700, color:"#475569", whiteSpace:"nowrap", flexShrink:0, letterSpacing:"0.5px" }}>+880</span>
                  <input className={`input ${errors.phoneDigits ? "input-error" : ""}`}
                    style={{ borderRadius:"0 6px 6px 0", borderLeft:"none", flex:1 }}
                    value={form.phoneDigits}
                    onChange={e => { const val = e.target.value.replace(/\D/g,"").slice(0,10); set("phoneDigits",val); }}
                    placeholder="1712345678" maxLength={10} inputMode="numeric" />
                </div>
                {errors.phoneDigits
                  ? <div style={{ color:"#DC2626", fontSize:12, marginTop:3 }}>{errors.phoneDigits}</div>
                  : <div style={{ fontSize:11, marginTop:3, color: phoneResult.valid ? "#16A34A" : "#94A3B8" }}>
                      {phoneResult.valid ? `✓ ${phoneResult.normalised}` : form.phoneDigits.length > 0 ? `${form.phoneDigits.length}/10 digits` : "10 digits starting with 1 — e.g. 1712345678"}
                    </div>}
              </div>

              {dupChecking && <div style={{ fontSize:12, color:"#94A3B8", marginBottom:10 }}>🔍 Checking for existing patients...</div>}
              {!dupChecking && duplicates.length > 0 && !proceedAnyway && (
                <div style={{ background:"#FFF7ED", border:"1.5px solid #FB923C", borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
                  <div style={{ fontWeight:700, color:"#C2410C", fontSize:13, marginBottom:8 }}>⚠️ Possible duplicate patient{duplicates.length > 1 ? "s" : ""} found</div>
                  {duplicates.map(dup => (
                    <div key={dup.id} style={{ background:"#fff", border:"1px solid #FED7AA", borderRadius:7, padding:"8px 10px", marginBottom:6, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{dup.name}</div>
                        <div style={{ fontSize:11, color:"#64748B" }}>{dup.id} · {dup.age ? dup.age+"y" : "—"} · {dup.sex || "—"} · {dup.phone || "—"}{dup.doctor ? ` · ${dup.doctor}` : ""}</div>
                        <div style={{ fontSize:11, color:"#92400E", marginTop:2 }}>{dup.match_reason}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setProceedAnyway(true); setErrors({}); }} type="button">Register as new patient anyway</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { const pid = duplicates[0]?.id; if (pid) { sessionStorage.setItem("emr_selected_patient", pid); if (typeof onOpenPatient === "function") onOpenPatient(pid); else window.location.reload(); } }} type="button">Open existing patient →</button>
                  </div>
                </div>
              )}
              {!dupChecking && duplicates.length > 0 && proceedAnyway && (
                <div style={{ background:"#F0FDF4", border:"1px solid #86EFAC", borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:12, color:"#166534" }}>✓ Registering as new patient (duplicate acknowledged)</div>
              )}
              {errors._dup && <div style={{ color:"#DC2626", fontSize:12, marginBottom:10 }}>{errors._dup}</div>}

              <div className="form-group">
                <label className="form-label">Address *</label>
                <input className={"input " + (errors.address ? "input-error" : "")} value={form.address} onChange={e => set("address",e.target.value)} placeholder="Full address" />
                {errors.address && <div style={{ color:"#DC2626", fontSize:12, marginTop:3 }}>{errors.address}</div>}
              </div>
            </div>
          )}

          {step === 2 && (() => {
            const CONDITION_SUGGESTIONS = ["Diabetes","Hypertension","Asthma","Heart Disease","Kidney Disease","Thyroid","Epilepsy","COPD","Arthritis","Hepatitis B","Hepatitis C"];
            const addCondition = (val) => {
              const v = val.trim();
              if (!v || form.conditions.includes(v)) { setConditionInput(""); return; }
              set("conditions", [...form.conditions, v]);
              setConditionInput("");
            };
            const inputSt = { padding:"9px 12px", border:"1.5px solid #CBD5E1", borderRadius:8, fontSize:13, width:"100%", fontFamily:"inherit", outline:"none", background:"#fff", boxSizing:"border-box" };
            const labelSt = { display:"block", fontSize:11, fontWeight:600, color:"#64748B", marginBottom:5, textTransform:"uppercase", letterSpacing:".05em" };
            const secSt = { fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8", margin:"18px 0 10px", paddingBottom:6, borderBottom:"1px solid #F1F5F9" };
            return (
              <div>
                <div style={secSt}>Clinical</div>
                <div className="form-group">
                  <label className="form-label">Referred Doctor *</label>
                  <input className={"input " + (errors.doctor ? "input-error" : "")} value={form.doctor} onChange={e => set("doctor",e.target.value)} placeholder="Dr. Name (MBBS)" />
                  {errors.doctor && <div style={{ color:"#DC2626", fontSize:12, marginTop:3 }}>{errors.doctor}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Emergency Contact</label>
                  <input className="input" value={form.emergency_contact} onChange={e => set("emergency_contact",e.target.value)} placeholder="Name — Phone" />
                </div>
                <div style={secSt}>Known Conditions <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, fontSize:10 }}>(auto-fills Clinical History)</span></div>
                <div className="form-group">
                  <label style={labelSt}>Chronic Conditions</label>
                  <div style={{ border:"1.5px solid #CBD5E1", borderRadius:8, padding:"8px 10px", display:"flex", flexWrap:"wrap", gap:6, cursor:"text", minHeight:44, background:"#fff" }}
                    onClick={e => e.currentTarget.querySelector("input")?.focus()}>
                    {form.conditions.map(c => (
                      <span key={c} style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#EEF2FF", color:"#4338CA", borderRadius:99, padding:"3px 10px", fontSize:12, fontWeight:600 }}>
                        {c}
                        <span style={{ cursor:"pointer", opacity:.7, fontSize:13 }} onClick={() => set("conditions", form.conditions.filter(x => x !== c))}>×</span>
                      </span>
                    ))}
                    <input style={{ border:"none", outline:"none", background:"none", padding:0, fontSize:13, minWidth:120, flex:1 }}
                      placeholder="Type and press Enter…" value={conditionInput}
                      onChange={e => setConditionInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCondition(conditionInput); } }}/>
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:6 }}>
                    {CONDITION_SUGGESTIONS.filter(s => !form.conditions.includes(s)).slice(0,6).map(s => (
                      <span key={s} style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:99, padding:"3px 10px", fontSize:11, color:"#475569", cursor:"pointer", fontWeight:500 }}
                        onClick={() => addCondition(s)}>+ {s}</span>
                    ))}
                  </div>
                </div>
                <div style={secSt}>Social History <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, fontSize:10 }}>(auto-fills Social History)</span></div>
                <div className="form-group">
                  <label style={labelSt}>Smoking Status</label>
                  <div style={{ display:"flex", gap:0, borderRadius:8, overflow:"hidden", border:"1.5px solid #E2E8F0", width:"fit-content" }}>
                    {["Never","Former","Current"].map(opt => (
                      <button key={opt} type="button" onClick={() => set("smokingStatus",opt)}
                        style={{ padding:"7px 18px", fontSize:13, fontWeight: form.smokingStatus===opt ? 700 : 400, border:"none", borderRight:"1px solid #E2E8F0", cursor:"pointer", color: form.smokingStatus===opt ? (opt==="Never"?"#475569": opt==="Former"?"#92400E":"#DC2626") : "#64748B", background: form.smokingStatus===opt ? (opt==="Never"?"#F1F5F9": opt==="Former"?"#FEF3C7":"#FEE2E2") : "#fff" }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                  {form.smokingStatus !== "Never" && (
                    <input style={{ ...inputSt, marginTop:8, background:"#FFFBEB", borderColor:"#FDE68A" }}
                      placeholder="Frequency / duration — e.g. ½ pack per day, 10 years"
                      value={form.smokingDetail} onChange={e => set("smokingDetail",e.target.value)}/>
                  )}
                </div>
                <div className="form-group">
                  <label style={labelSt}>Betel Nut / Jorda</label>
                  <div style={{ display:"flex", gap:0, borderRadius:8, overflow:"hidden", border:"1.5px solid #E2E8F0", width:"fit-content" }}>
                    {["No","Yes"].map(opt => (
                      <button key={opt} type="button" onClick={() => set("betelNut",opt)}
                        style={{ padding:"7px 22px", fontSize:13, fontWeight: form.betelNut===opt ? 700 : 400, border:"none", borderRight:"1px solid #E2E8F0", cursor:"pointer", color: form.betelNut===opt ? (opt==="No"?"#475569":"#DC2626") : "#64748B", background: form.betelNut===opt ? (opt==="No"?"#F1F5F9":"#FEE2E2") : "#fff" }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                  {form.betelNut === "Yes" && (
                    <input style={{ ...inputSt, marginTop:8, background:"#FFFBEB", borderColor:"#FDE68A" }}
                      placeholder="Frequency — e.g. daily, occasionally"
                      value={form.betelNutDetail} onChange={e => set("betelNutDetail",e.target.value)}/>
                  )}
                </div>
                <div style={secSt}>Tests Ordered <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, fontSize:10 }}>(select all tests the patient needs)</span></div>
                <div className="form-group">
                  {form.ordered_tests && form.ordered_tests.length > 0 && (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:8 }}>
                      {form.ordered_tests.map(t => (
                        <span key={t} style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#EFF6FF", color:"#1D4ED8", border:"1px solid #BFDBFE", borderRadius:99, padding:"3px 10px", fontSize:12, fontWeight:500 }}>
                          {t}
                          <span style={{ cursor:"pointer", opacity:.7 }} onClick={() => toggleTest(t)}>×</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Search box */}
                  <div style={{ position:"relative", marginBottom:6 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      style={{ width:"100%", padding:"8px 10px 8px 30px", border:"1.5px solid #E2E8F0", borderRadius:8, fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
                      placeholder="Search tests…"
                      value={testSearch}
                      onChange={e => setTestSearch(e.target.value)}
                    />
                    {testSearch && (
                      <span onClick={() => setTestSearch("")} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", cursor:"pointer", color:"#94A3B8", fontSize:16 }}>×</span>
                    )}
                  </div>
                  {testGroups.length === 0 ? (
                    <div style={{ color:"#94A3B8", fontSize:12 }}>Loading tests…</div>
                  ) : (() => {
                    const q = testSearch.trim().toLowerCase();
                    const filtered = q
                      ? testGroups.map(g => ({ ...g, subtypes: g.subtypes.filter(s => s.toLowerCase().includes(q)) })).filter(g => g.subtypes.length > 0)
                      : testGroups;
                    return (
                      <div style={{ border:"1.5px solid #E2E8F0", borderRadius:8, maxHeight:220, overflowY:"auto", padding:"4px 0" }}>
                        {filtered.length === 0 ? (
                          <div style={{ padding:"16px 12px", color:"#94A3B8", fontSize:13, textAlign:"center" }}>No tests found</div>
                        ) : filtered.map(group => (
                          <div key={group.category}>
                            <div style={{ padding:"6px 12px 2px", fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.05em", background:"#F8FAFC", borderBottom:"0.5px solid #E2E8F0" }}>
                              {group.category}
                            </div>
                            {group.subtypes.map(sub => (
                              <div key={sub}
                                onClick={() => toggleTest(sub)}
                                style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 12px", cursor:"pointer", background: form.ordered_tests.includes(sub) ? "#EFF6FF" : "transparent", transition:"background 0.1s" }}
                                onMouseEnter={e => { if (!form.ordered_tests.includes(sub)) e.currentTarget.style.background="#F8FAFC"; }}
                                onMouseLeave={e => { if (!form.ordered_tests.includes(sub)) e.currentTarget.style.background="transparent"; }}>
                                <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${form.ordered_tests.includes(sub) ? "#2563EB" : "#CBD5E1"}`, background: form.ordered_tests.includes(sub) ? "#2563EB" : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.1s" }}>
                                  {form.ordered_tests.includes(sub) && (
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                                  )}
                                </div>
                                <span style={{ fontSize:13, color: form.ordered_tests.includes(sub) ? "#1D4ED8" : "#374151", fontWeight: form.ordered_tests.includes(sub) ? 500 : 400 }}>{sub}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <div style={secSt}>Confirmation</div>
                <div style={{ background:"#F8FAFC", border:"1px solid #E4E8F0", borderRadius:10, padding:"12px 14px", marginBottom:8 }}>
                  {[
                    ["Name",    form.name],
                    ["DOB/Age", form.dobUnknown ? (form.age+"Y (estimated)") : (form.dob ? `${form.dob} (${ageDisplay || ""})` : "—")],
                    ["Sex",     form.sex],
                    ["Blood",   form.blood],
                    ["Phone",   phonePreview],
                  ].map(([l,v]) => (
                    <div key={l} style={{ display:"flex", gap:8, padding:"4px 0", fontSize:13 }}>
                      <span style={{ color:"#64748B", minWidth:70 }}>{l}:</span>
                      <span style={{ fontWeight:600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="modal-footer">
          {step === 1 ? (
            <>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={goNext}>Next →</button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
              <button className="btn btn-primary" onClick={submit} disabled={loading}>
                {loading ? "Registering…" : "✓ Register Patient"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}